import React from 'react';
import { Check, Info, Phone as PhoneIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { pricing, additionalInfo, coverageNote, doctorInfo } from '../data/mock';

const Pricing = () => {
  const handleContact = (customMessage) => {
    const message = customMessage || doctorInfo.whatsappMessage;
    window.open(`https://wa.me/${doctorInfo.whatsapp}?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <section id="tarifas" className="py-24 bg-gradient-to-b from-white via-[#3D2418]/6 to-[#3D2418]/10 relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#7DAAAF]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#3D2418]/5 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-12 animate-in">
          <div className="inline-block mb-4">
            <span className="text-[#7DAAAF] font-semibold text-sm tracking-wider uppercase">Tarifas</span>
          </div>
          <div className="w-32 h-1.5 bg-gradient-to-r from-[#7DAAAF] to-[#3D2418] rounded-full mx-auto mb-6"></div>
          
          {/* Coverage Note */}
          <div className="max-w-3xl mx-auto bg-[#7DAAAF]/10 border border-[#7DAAAF]/20 rounded-2xl p-4 mb-8">
            <p className="text-sm text-[#3D2418] font-medium flex items-start justify-center">
              <Info className="w-5 h-5 text-[#7DAAAF] mr-2 flex-shrink-0 mt-0.5" />
              {coverageNote}
            </p>
          </div>
        </div>

        {/* Main Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {pricing.slice(0, 3).map((plan) => (
            <Card 
              key={plan.id}
              className={`relative flex flex-col ${
                plan.highlighted 
                  ? 'border-3 border-[#7DAAAF] shadow-2xl scale-105 bg-gradient-to-b from-white to-[#7DAAAF]/5' 
                  : 'border-2 border-gray-200 hover:border-[#7DAAAF] hover:shadow-xl'
              } transition-all duration-300 rounded-2xl overflow-hidden`}
            >
              {plan.highlighted && (
                <div className="absolute top-0 left-0 right-0 bg-[#7DAAAF] text-white text-center py-2 text-sm font-bold">
                  Más Solicitado
                </div>
              )}
              
              <CardHeader className={`text-center ${plan.highlighted ? 'pt-12' : 'pt-8'} pb-4`}>
                <CardTitle className="text-2xl font-bold text-[#3D2418] mb-4">{plan.type}</CardTitle>
                <div className="mb-4">
                  <span className="text-5xl font-bold text-[#7DAAAF]">{plan.price}</span>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{plan.description}</p>
              </CardHeader>
              
              <CardContent className="px-6 pb-8 flex flex-col flex-grow">
                {plan.features.length > 0 && (
                  <ul className="space-y-3 mb-6 flex-grow">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="w-5 h-5 text-[#7DAAAF] mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}
                
                <Button 
                  onClick={() => handleContact(plan.whatsappMessage)}
                  data-testid={`pricing-cta-${plan.id}`}
                  className={`w-full mt-auto ${
                    plan.highlighted 
                      ? 'bg-[#7DAAAF] hover:bg-[#6a959a] text-white' 
                      : 'bg-white border-2 border-[#7DAAAF] text-[#7DAAAF] hover:bg-[#7DAAAF] hover:text-white'
                  } font-semibold py-6`}
                >
                  Solicitar Cita
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Services */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {pricing.slice(3).map((plan) => (
            <Card 
              key={plan.id}
              className="border-2 border-gray-200 hover:border-[#7DAAAF] hover:shadow-xl transition-all duration-300 rounded-2xl"
            >
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl font-bold text-[#3D2418] mb-2">{plan.type}</CardTitle>
                    <p className="text-sm text-gray-600">{plan.description}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-3xl font-bold text-[#7DAAAF]">{plan.price}</span>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <Button 
                  onClick={() => handleContact(plan.whatsappMessage)}
                  data-testid={`pricing-extra-cta-${plan.id}`}
                  className="w-full bg-white border-2 border-[#7DAAAF] text-[#7DAAAF] hover:bg-[#7DAAAF] hover:text-white font-semibold"
                >
                  Solicitar Cita
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Information */}
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 md:p-10 border-2 border-gray-100 shadow-lg">
          <h3 className="text-2xl font-bold text-[#3D2418] mb-8 flex items-center justify-center">
            <Info className="w-6 h-6 text-[#7DAAAF] mr-3" />
            Información Adicional
          </h3>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Left Column - Payment & Billing */}
            <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
              <h4 className="font-bold text-[#3D2418] mb-4 text-lg flex items-center">
                <div className="w-2 h-2 bg-[#7DAAAF] rounded-full mr-3"></div>
                Facturación y Pagos
              </h4>
              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-[#7DAAAF] rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700 text-sm leading-relaxed">{additionalInfo[0]}</p>
                </div>
                <div className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-[#7DAAAF] rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700 text-sm leading-relaxed">{additionalInfo[1]}</p>
                </div>
                <div className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-[#7DAAAF] rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700 text-sm leading-relaxed">{additionalInfo[2]}</p>
                </div>
              </div>
            </div>

            {/* Right Column - Services & Conditions */}
            <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
              <h4 className="font-bold text-[#3D2418] mb-4 text-lg flex items-center">
                <div className="w-2 h-2 bg-[#7DAAAF] rounded-full mr-3"></div>
                Condiciones del Servicio
              </h4>
              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-[#7DAAAF] rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700 text-sm leading-relaxed">{additionalInfo[3]}</p>
                </div>
                <div className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-[#7DAAAF] rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700 text-sm leading-relaxed">{additionalInfo[4]}</p>
                </div>
                <div className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-[#7DAAAF] rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700 text-sm leading-relaxed">{additionalInfo[5]}</p>
                </div>
                <div className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-[#7DAAAF] rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700 text-sm leading-relaxed">{additionalInfo[6]}</p>
                </div>
                <div className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-[#7DAAAF] rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700 text-sm leading-relaxed">{additionalInfo[7]}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
